// Mini simülasyon paneli: bir izleyici profili gir, mevcut form'daki reklam
// (henüz kaydedilmemiş bile olabilir) o profile karşı kaç puan alır ve diğer
// reklamlar arasında kaçıncı sırada olduğunu göster.

import {
  AGE_BRACKETS,
  AGE_BRACKET_LABELS_TR,
  GENDERS,
  GENDER_LABELS_TR,
  GROUP_TYPES,
  GROUP_TYPE_LABELS_TR,
} from "../constants.js";
import { getAds } from "../ads-state.js";
import { scoreAd, rankAds } from "../scoring.js";

function escapeHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

let _profile = { age: "(24-30)", gender: "male", group_type: "solo" };

function chipPicker(name, values, labels, currentValue) {
  return values.map((v) => `
    <button type="button" data-sim-field="${name}" data-sim-value="${escapeHtml(v)}"
            class="px-2 py-0.5 rounded text-[10px] transition-colors ${
              v === currentValue
                ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/50"
                : "bg-slate-800 text-slate-400 border border-transparent hover:bg-slate-700"
            }">
      ${escapeHtml(labels[v] || v)}
    </button>`).join("");
}

function badge(text, color) {
  const cls = {
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    red: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    slate: "bg-slate-700 text-slate-400 border-slate-600",
  }[color];
  return `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-bold border ${cls}">${escapeHtml(text)}</span>`;
}

function ageMatchBadge(m) {
  if (m === "exact") return badge("yaş tam", "green");
  if (m === "neighbor") return badge("yaş komşu", "amber");
  if (m === "no_target") return badge("hedefsiz", "slate");
  return badge("yaş uymaz", "red");
}

function genderMatchBadge(m) {
  return m ? badge("cinsiyet ✓", "green") : badge("cinsiyet ×", "red");
}

function groupMatchBadge(m) {
  if (m === "exact") return badge("grup ✓", "green");
  if (m === "no_target_group") return badge("grup serbest", "amber");
  if (m === "no_target") return badge("hedefsiz", "slate");
  return badge("grup ×", "red");
}

function collectCurrentAdFromForm(formEl) {
  const fd = new FormData(formEl);
  return {
    id: (fd.get("id") || "").toString() || "(yeni)",
    title: (fd.get("title") || "").toString() || "Bu Reklam",
    age_brackets: fd.getAll("age_brackets"),
    genders: fd.getAll("genders"),
    group_types: fd.getAll("group_types"),
    priority: parseInt(fd.get("priority"), 10) || 5,
  };
}

export function renderSimulatorHtml() {
  return `
    <details id="simulator-panel" class="group bg-slate-900/40 border border-slate-800 rounded-lg overflow-hidden">
      <summary class="cursor-pointer px-4 py-2.5 flex items-center justify-between hover:bg-slate-900/80 select-none">
        <div class="flex items-center gap-2">
          <span class="group-open:rotate-90 transition-transform inline-block text-cyan-400">▶</span>
          <h4 class="text-xs uppercase tracking-widest text-cyan-400 font-bold">🎯 Simülasyon</h4>
          <span class="text-[10px] text-slate-500 italic">bir profile karşı bu reklam ne yapıyor?</span>
        </div>
        <span class="text-[9px] text-slate-600 hidden group-open:inline">aç/kapat</span>
      </summary>
      <div class="px-4 py-3 space-y-3 border-t border-slate-800">
        <div class="grid grid-cols-3 gap-3 text-[10px]">
          <div class="space-y-1">
            <div class="uppercase tracking-widest text-slate-500">Yaş</div>
            <div id="sim-age-picker" class="flex flex-wrap gap-1">${chipPicker("age", AGE_BRACKETS, AGE_BRACKET_LABELS_TR, _profile.age)}</div>
          </div>
          <div class="space-y-1">
            <div class="uppercase tracking-widest text-slate-500">Cinsiyet</div>
            <div id="sim-gender-picker" class="flex flex-wrap gap-1">${chipPicker("gender", GENDERS, GENDER_LABELS_TR, _profile.gender)}</div>
          </div>
          <div class="space-y-1">
            <div class="uppercase tracking-widest text-slate-500">Grup</div>
            <div id="sim-group-picker" class="flex flex-wrap gap-1">${chipPicker("group_type", GROUP_TYPES, GROUP_TYPE_LABELS_TR, _profile.group_type)}</div>
          </div>
        </div>
        <div id="sim-result" class="space-y-2"></div>
      </div>
    </details>`;
}

function updateChipPicker(containerId, fieldName, currentValue) {
  const c = document.getElementById(containerId);
  if (!c) return;
  c.querySelectorAll(`[data-sim-field="${fieldName}"]`).forEach((btn) => {
    const active = btn.dataset.simValue === currentValue;
    btn.className = `px-2 py-0.5 rounded text-[10px] transition-colors ${
      active
        ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/50"
        : "bg-slate-800 text-slate-400 border border-transparent hover:bg-slate-700"
    }`;
  });
}

function renderResult(currentAd, allAds) {
  const me = scoreAd(currentAd, _profile);
  const allWithSelf = [currentAd, ...allAds.filter((a) => a.id !== currentAd.id)];
  const ranked = rankAds(allWithSelf, _profile);
  const myIndex = ranked.findIndex((r) => r.ad.id === currentAd.id);
  const myRank = myIndex + 1;
  const topAd = ranked[0];

  const scoreColor = me.raw_score >= 0.7 ? "emerald" : me.raw_score >= 0.4 ? "amber" : "red";
  const scoreCls = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    red: "text-rose-300",
  }[scoreColor];

  let verdict;
  if (me.raw_score === 0) {
    verdict = "Bu profile <strong>hiç</strong> çıkmaz (puan 0)";
  } else if (myRank === 1) {
    verdict = `Bu profile <strong>en uygun</strong> reklam → en sık bu çıkar`;
  } else if (myRank <= 3) {
    verdict = `<strong>${myRank}. sırada</strong> — top-3'te, sık çıkar`;
  } else if (myRank <= 5) {
    verdict = `${myRank}. sırada — top-5'te, ara sıra çıkar`;
  } else {
    verdict = `${myRank}. sırada — çok arkada, nadiren çıkar`;
  }

  const topAds = ranked.slice(0, 5);

  return `
    <div class="bg-slate-900 border border-slate-800 rounded p-3 space-y-2">
      <div class="flex items-baseline gap-3 flex-wrap">
        <div>
          <div class="text-[9px] uppercase tracking-widest text-slate-500">Skor</div>
          <div class="text-3xl font-black tabular-nums ${scoreCls}">${me.raw_score.toFixed(3)}</div>
        </div>
        <div class="flex-1">
          <div class="text-[9px] uppercase tracking-widest text-slate-500">Karar</div>
          <div class="text-xs text-slate-200">${verdict}</div>
          <div class="text-[10px] text-slate-500 italic">${allWithSelf.length} reklam arasında sıralandı</div>
        </div>
      </div>
      <div class="flex flex-wrap gap-1 pt-1">
        ${ageMatchBadge(me.age_match)}
        ${genderMatchBadge(me.gender_match)}
        ${groupMatchBadge(me.group_match)}
        ${badge(`öncelik ×${me.priority_mult.toFixed(2)}`, me.priority_mult >= 0.9 ? "green" : me.priority_mult >= 0.65 ? "amber" : "slate")}
      </div>
      ${topAds.length > 1 ? `
        <details class="pt-1">
          <summary class="cursor-pointer text-[10px] text-slate-500 hover:text-cyan-400 select-none">
            Top-5 sıralama (rakipler dahil)
          </summary>
          <table class="w-full mt-1.5 text-[10px]">
            ${topAds.map((r, i) => `
              <tr class="${r.ad.id === currentAd.id ? "bg-cyan-500/10 font-bold text-cyan-300" : "text-slate-400"}">
                <td class="py-0.5 pr-2 tabular-nums w-6">${i + 1}.</td>
                <td class="py-0.5 pr-2">${escapeHtml(r.ad.title || r.ad.id)}</td>
                <td class="py-0.5 tabular-nums text-right">${r.breakdown.raw_score.toFixed(3)}</td>
              </tr>`).join("")}
          </table>
        </details>` : ""}
    </div>`;
}

export function refreshSimulator(formEl) {
  const result = document.getElementById("sim-result");
  if (!result || !formEl) return;
  const currentAd = collectCurrentAdFromForm(formEl);
  const allAds = getAds();
  result.innerHTML = renderResult(currentAd, allAds);
}

export function bindSimulator(formEl) {
  // Profile picker click delegation (modal yeniden açıldıkça yeni instance gelir)
  const panel = document.getElementById("simulator-panel");
  if (!panel) return;
  panel.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-sim-field]");
    if (!btn) return;
    const field = btn.dataset.simField;
    const value = btn.dataset.simValue;
    if (_profile[field] === value) return;
    _profile[field] = value;
    if (field === "age") updateChipPicker("sim-age-picker", "age", value);
    if (field === "gender") updateChipPicker("sim-gender-picker", "gender", value);
    if (field === "group_type") updateChipPicker("sim-group-picker", "group_type", value);
    refreshSimulator(formEl);
  });
  refreshSimulator(formEl);
}
